(ns arthur.ui.shortcuts
  "Every key the window answers to, as one table, and the sheet that lists them.

  A COMMAND IS DATA: what it is called, which keys reach it, and a function of
  the db that answers the event to send, or nil for nothing to do. One listener
  reads the table, the `?` sheet prints it, and nothing else in the app listens
  to the window for keys. What a key does in one element only — nudging the
  timeline's selected keys, Enter in a rename field — stays on that element,
  which handles it first and stops it, as Blender's modal operators do.

  NO MODES. A key means one thing everywhere and what it acts on is the
  selection, so there is no state to remember besides the tool. Space is the one
  key with two meanings, and they are told apart by the hand rather than by a
  mode: tapped it plays, held and dragged on the stage it pans, as in Photoshop.

  THE KEYS ARE BLENDER'S, then Resolve's, then Flash's and Harmony's. WASD repeats the arrows
  for a hand that stays on the left of the keyboard while the other holds the
  pen. Letters are matched by where they are on the keyboard, not by what they
  type, so WASD is WASD on any layout.

  SHIFT IS THE ONE MODIFIER, and ⌘ only where the browser's own convention is
  (undo, the clipboard). Alt types characters on a Mac and is the browser's
  elsewhere, so nothing here uses it — a key with it passes through untouched."
  (:require [arthur.events.history :as history]
            [arthur.events.playback :as pb]
            [arthur.events.ui :as ui]
            [arthur.ui.layout :as layout]
            [arthur.ui.timeline :as timeline]
            [clojure.string :as str]
            [re-frame.core :as rf]
            [re-frame.db :as db]
            [reagent.core :as r]))

(defn- frame [db] (get-in db [:playback :frame] 0))
(defn- seek [f] [::pb/seek f])

(def commands
  [["Time"
    [{:label "Play / pause" :keys ["Space"] :run (constantly [::pb/toggle])
      :note "hold and drag on the stage to pan"}
     {:label "Previous frame" :keys ["ArrowLeft" "a"] :run #(seek (dec (frame %)))}
     {:label "Next frame" :keys ["ArrowRight" "d"] :run #(seek (inc (frame %)))}
     {:label "Previous key" :keys ["ArrowDown" "s"] :run timeline/prev-key}
     {:label "Next key" :keys ["ArrowUp" "w"] :run timeline/next-key}
     {:label "First frame" :keys ["Home" "shift+ArrowLeft" "shift+a"] :run (constantly (seek 0))}
     {:label "Last frame" :keys ["End" "shift+ArrowRight" "shift+d"] :run timeline/last-frame}
     {:label "Mark in" :keys ["i"] :run (constantly [::pb/mark :in])
      :note "playback loops between the marks"}
     {:label "Mark out" :keys ["o"] :run (constantly [::pb/mark :out])}
     {:label "Clear in" :keys ["shift+i"] :run (constantly [::pb/clear-mark :in])}
     {:label "Clear out" :keys ["shift+o"] :run (constantly [::pb/clear-mark :out])}
     {:label "Tracing" :keys ["t"]
      :run #(vector ::ui/tracing-on (not (get-in % [:ui :tracing :on?])))}
     {:label "Tracing for the selection only" :keys ["shift+t"]
      :run (constantly [::ui/trace-selection])}
     {:label "Onion skin" :keys ["g"] :note "ghosts"
      :run #(let [on? (get-in % [:ui :onion (:clip/current %) (get-in % [:ui :open]) :on?])]
              [::layout/onion :on? (not on?)])}]]
   ["Animate"
    [{:label "Key here" :keys ["k"]
      :note "in a lane, a copy of the drawing from the playhead on"
      :run timeline/key-here}
     {:label "Blank drawing here" :keys ["shift+k"] :run (constantly [::ui/drawing-here false])}
     {:label "One frame longer" :keys ["=" "+"] :note "moves the end of the selection's span"
      :run (timeline/hold 1)}
     {:label "One frame shorter" :keys ["-"]
      :run (timeline/hold -1)}]]
   ["Make"
    [{:label "New symbol" :keys ["n"] :run (constantly [::ui/new :inside false])
      :note "in the selected symbol; in a lane, a blank drawing"}
     {:label "New lane" :keys ["shift+n"] :run (constantly [::ui/new :inside true])}
     {:label "New symbol at the top of the tab" :keys ["m"] :run (constantly [::ui/new :top false])}
     {:label "New lane at the top of the tab" :keys ["shift+m"] :run (constantly [::ui/new :top true])}]]
   ["Draw"
    [{:label "Select" :keys ["v"] :run (constantly [::ui/tool-key "v"])}
     {:label "Pen" :keys ["p"] :run (constantly [::ui/tool-key "p"])}
     {:label "Brush" :keys ["b"] :run (constantly [::ui/tool-key "b"])}
     {:label "Eraser" :keys ["e"] :run (constantly [::ui/tool-key "e"])}
     {:label "Smaller brush" :keys ["["] :run (constantly [::ui/tool-key "["])}
     {:label "Bigger brush" :keys ["]"] :run (constantly [::ui/tool-key "]"])}
     {:label "Close the pen's shape" :keys ["Enter"] :run (constantly [::ui/tool-key "enter"])}
     {:label "Leave the pen" :keys ["Escape"] :run (constantly [::ui/tool-key "escape"])}]]
   ["Edit"
    [{:label "Undo" :keys ["mod+z"] :run (constantly [::history/undo])}
     {:label "Redo" :keys ["mod+shift+z" "mod+y"] :run (constantly [::history/redo])}
     {:label "Copy" :keys ["mod+c"] :run (constantly [::ui/copy])}
     {:label "Cut" :keys ["mod+x"] :run (constantly [::ui/cut])}
     {:label "Paste" :keys ["mod+v"] :run (constantly [::ui/paste])}
     {:label "Duplicate" :keys ["mod+d"] :run (constantly [::ui/duplicate])}
     {:label "Duplicate as new symbol" :keys ["mod+shift+d"] :run (constantly [::ui/duplicate-unique])}
     {:label "Delete" :keys ["Delete" "Backspace"] :run (constantly [::ui/delete-selected])}]]
   ["Help"
    [{:label "These shortcuts" :keys ["?"]}]]])

(def ^:private by-chord
  (into {} (for [[_ cs] commands c cs k (:keys c)] [k c])))

(defn- chord
  "The key as the table spells it: `mod+` for ⌘ or Ctrl, then `alt+`, then
  `shift+` — except on a character that shift itself typed, like `?`."
  [^js e]
  (let [code (.-code e)
        letter (when (str/starts-with? code "Key") (str/lower-case (subs code 3)))
        k (cond letter letter (= "Space" code) "Space" :else (.-key e))]
    (str (when (or (.-metaKey e) (.-ctrlKey e)) "mod+")
         (when (.-altKey e) "alt+")
         (when (and (.-shiftKey e) (or letter (< 1 (count k)))) "shift+")
         k)))

(defn- typing?
  "Is a key going into a field? A slider or a checkbox has focus without taking
  letters, so the window's keys still work there, as a tool's key does in
  Photoshop."
  [^js target]
  (or (#{"TEXTAREA" "SELECT"} (.-tagName target))
      (and (= "INPUT" (.-tagName target))
           (not (#{"range" "checkbox" "radio" "button" "color"} (.-type target))))
      (.-isContentEditable target)))

(defn- press!
  "Send what key `k` answers now, against the db as it is.

  SYNCHRONOUSLY, so a held arrow counts every repeat. Dispatched instead, two
  presses in quick succession both read the frame before either had moved it,
  and D D went one frame."
  [k]
  (when-let [ev (some-> (get-in by-chord [k :run]) (#(% @db/app-db)))]
    (rf/dispatch-sync ev)))

;; Nil, :held, or :panned once a drag on the stage has used the hold — see
;; `ui/stage`. A tap is a hold that was never used.
(defonce space (atom nil))
(defonce ^:private open? (r/atom false))

(defn- on-key-down [^js e]
  (when-not (typing? (.-target e))
    (let [k (chord e)]
      (cond
        (and @open? (#{"Escape" "?"} k)) (do (.preventDefault e) (reset! open? false))
        (= "?" k) (do (.preventDefault e) (reset! open? true))
        ;; Played on the way UP, because only then is it known to be a tap.
        ;; Default prevented both ways, or a focused button clicks as well.
        (= "Space" k) (do (.preventDefault e)
                          (when-not (.-repeat e) (reset! space :held)))
        (by-chord k) (do (.preventDefault e) (press! k))))))

(defn- on-key-up [^js e]
  (when (= "Space" (.-code e))
    (when (= :held @space)
      (.preventDefault e)
      (press! "Space"))
    (reset! space nil)))

(defn install! []
  (.addEventListener js/window "keydown" on-key-down)
  (.addEventListener js/window "keyup" on-key-up)
  (.addEventListener js/window "blur" #(reset! space nil)))

(def ^:private glyphs
  {"mod" "⌘" "shift" "⇧" "ArrowLeft" "←" "ArrowRight" "→"
   "ArrowUp" "↑" "ArrowDown" "↓" "Backspace" "⌫" "Escape" "Esc"})

(defn- spell [k]
  (apply str (map #(or (glyphs %) (str/upper-case %)) (str/split k #"\+"))))

(defn sheet
  "The `?` sheet. Shut by Esc, by `?` again, or by a click anywhere off it."
  []
  (when @open?
    [:<>
     [:div.keys-scrim {:on-click #(reset! open? false)}]
     [:section.keys-sheet {:role "dialog" :aria-modal true :aria-label "Keyboard shortcuts"}
      (for [[title cs] commands]
        ^{:key title}
        [:div.keys-group
         [:h3 title]
         (for [{:keys [label note] ks :keys} cs]
           ^{:key label}
           [:div.keys-row
            [:span label (when note [:small note])]
            [:span.keys (for [k ks] ^{:key k} [:kbd (spell k)])]])])]]))

(defn button []
  [:button {:title "keyboard shortcuts (?)" :aria-label "keyboard shortcuts"
                :on-click #(reset! open? true)} "?"])
